import { useNavigate } from '@tanstack/react-router'
import { Button } from 'antd'
import { ArrowLeft } from '../components/ArrowLeft'
import { Card } from '../components/Card'
import { OtpForm } from '../components/OtpForm'
import { ResendCodeButton } from '../components/ResendCodeButton'
import { StepBadge } from '../components/StepBadge'

/** Mask an email for display: first 4 chars of the local part, then `...@domain`. */
function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@')
  if (!domain) return email
  const visible = localPart.slice(0, 4)
  return `${visible}...@${domain}`
}

/**
 * Step 1 of 4: Check Your Email (OTP Verification).
 * Ported from the web client's `/login/verify-email` page.
 */
export function VerifyEmailPage({ email }: { email?: string }) {
  const navigate = useNavigate()
  const maskedEmail = email ? maskEmail(email) : 'jane...@alex.com'

  return (
    <Card>
      {/* 1. Accessible Step Badge */}
      <StepBadge step={1} />

      {/* 2. Page Header */}
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold tracking-tight text-brand-dark-text">Check your email</h1>
        <p className="text-sm text-brand-copyright leading-relaxed">
          Enter the 6-digit code sent to {maskedEmail}
        </p>
      </header>

      {/* 3. 6-Digit OTP Box Grid + Verify control */}
      <OtpForm mode="email" email={email} />

      {/* 4. Resend Code flow trigger */}
      <div className="flex justify-center items-center gap-1.5 text-sm">
        <span className="text-brand-copyright">Didn&apos;t receive it?</span>
        <ResendCodeButton email={email} />
      </div>

      <hr className="border-brand-border-light" />

      {/* 5. Back to Sign In Action */}
      <Button
        type="link"
        id="btn-otp-email-cancel"
        icon={<ArrowLeft />}
        className="text-xs font-semibold"
        onClick={() => navigate({ to: '/login' })}
      >
        Back to sign in
      </Button>
    </Card>
  )
}
